import { closeX } from './popup.js';
import { $, el } from '../state/store.js';
import { BUSINESS_TYPES, loadTrialStatus, sendTrialRequest, trialStatus } from '../services/trial.js';
import { currentUser, isAnonymous } from '../services/auth.js';
import { on } from '../state/bus.js';
import { showSignIn } from './account.js';
import { toast } from './toast.js';

/* A CONTACT FORM — the same .authwrap/.authcard shell as Feedback, Settings
   and Projects, for the same reason: this is a modal you open, say one thing
   in, and close.

   THERE IS NO TRIAL ANY MORE. Every tool, Motion included, is free to use
   without an account — nothing here is a locked surface somebody is asking
   past. What is still arranged by hand, until each has a self-serve path, is
   a credit pack (no checkout yet) and an Advanced licence (terms vary per
   customer), and this is the one form for both — no reason to split one
   person's question into two inboxes to reconcile later.

   Six fields, one of them already filled and not editable. What the customer
   intends to build is deliberately not asked — the terms conversation happens
   with a person, and a form that interrogates somebody before they have been
   answered reads as a qualification test rather than an introduction. The
   message box is there for anyone who wants to say more.

   THE EMAIL IS NOT A FIELD, and this is the one design decision here worth
   defending. motion_licences is keyed on email (0010), so a typed address
   that differs from the account's produces a licence which is granted,
   invisible to the customer, and reported back as "it still says locked". A
   validation message would only warn about that; taking the value from the
   session removes it. 0016's insert policy refuses a mismatch as well, so the
   guarantee does not depend on this file. */

/* COMING BACK AFTERWARDS.

   Asking without an account sends you to create one, and creating one is a
   magic link — so the page is left entirely and returned to on a fresh load,
   possibly in a different tab if the link is opened from a mail client.
   Nothing in module scope survives that, and without this the person lands
   back in the app signed in, with no sign of the thing they were in the
   middle of asking about, and has to go looking for the form again to start
   over. That is the point at which most people simply do not.

   The key keeps its old name (illusio3d.trial-resume) rather than being
   renamed along with everything else here — it is written to disk, named in
   PRIVACY.md and asserted by tools/verify.mjs, none of which a visitor ever
   sees, so renaming it would be paperwork with no reader.

   localStorage rather than sessionStorage for exactly the new-tab case;
   sessionStorage is per-tab and would be empty in the one the link opened.
   Stamped with a time and expired after an hour, so signing in next week for
   an unrelated reason does not reopen a form nobody asked for. */
const RESUME_KEY = 'illusio3d.trial-resume';
const RESUME_MAX_AGE = 3600_000;

/* ONE DOOR. This used to be two — a "Motion" one and a "licence" one, worded
   differently depending on which locked surface sent you here. Both surfaces
   are gone (nothing is locked), so both doors said the same true thing to two
   different headings, which is worse than picking one. `from` still arrives
   from every call site (a leftover of the two-door API, kept so none of them
   needed editing) and is still recorded as `source` on the row — worth having
   for your own sense of what prompted someone, even though nobody reads two
   different sentences depending on it any more. */
const CONTACT = {
  title: 'Contact us',
  lead: 'Credits and an Advanced licence are both still arranged directly — no '
      + 'self-serve checkout yet for either. Tell us what you need and terms are '
      + 'worked out individually for each customer.'
};
const door = () => CONTACT;

function markResume(from) {
  /* The door travels with the flag, so somebody returning from the sign-up
     link meets the same words they left. */
  try { localStorage.setItem(RESUME_KEY, Date.now() + '|' + (from || 'motion')); }
  catch (_) {}
}

/** True when the URL itself says to come back here — set on the magic link's
 *  emailRedirectTo, so it arrives with whoever opens it rather than depending
 *  on the storage of the browser that asked. Stripped once read, so a reload
 *  or a shared URL does not reopen the form.
 *
 *  The URL value is "contact" — renamed from "trial", which a visitor
 *  inspecting the landing page's own links (hover, right-click, devtools)
 *  could actually see, and there is no trial any more (see the file header).
 *  `from` still comes back as 'motion' below: that is this module's internal
 *  source tag, stored in the database and read by Admin, and renaming it
 *  is a different, bigger change than fixing what a visitor can see. */
function takeUrlIntent() {
  try {
    const u = new URL(location.href);
    const v = u.searchParams.get('next');
    if (v !== 'contact' && v !== 'licence') return null;
    u.searchParams.delete('next');
    history.replaceState(null, '', u.pathname + u.search + u.hash);
    return v === 'licence' ? 'licence' : 'motion';
  } catch (_) { return null; }
}

/** Reads it and clears it in one go — a resume that is offered twice is a
 *  loop, and this must fire once or not at all. */
function takeResume() {
  /* URL first and unconditionally, so the parameter is stripped even when a
     stored flag would also have fired — leaving ?next= in the address bar
     would reopen the form on the next reload. */
  const fromUrl = takeUrlIntent();
  let stored = null;
  try {
    const raw = localStorage.getItem(RESUME_KEY) || '';
    localStorage.removeItem(RESUME_KEY);
    const [t, from] = raw.split('|');
    if (Number(t) > 0 && Date.now() - Number(t) < RESUME_MAX_AGE) stored = from || 'motion';
  } catch (_) { /* storage unavailable; the URL may still have said so */ }
  return fromUrl || stored;
}

/* A magic link landing, as services/auth.js recognises one. */
const isAuthCallback = () =>
  location.hash.includes('access_token') || location.search.includes('code=');

on('auth:changed', async () => {
  const u = currentUser();
  /* Anonymous is not "signed in" here, and the stored flag is deliberately NOT
     consumed on the way past — an anonymous session is created before the
     real one and would otherwise eat the intent before it could be used.

     An intent that came in the URL is different, and this is the case the
     landing page's pricing CTAs create: somebody who has never opened the
     app, arriving with no account at all. Refusing them here left the
     button looking broken — the app loaded and nothing happened. showTrial()
     already knows how to meet them, with an explanation and a way to make an
     account, so hand it over rather than waiting for a real session that is
     never going to arrive on its own.

     Not while a magic link is landing, though: that flow creates an anonymous
     session before the real one, and opening the form against it would put
     "this needs an account first" in front of somebody in the middle of
     getting exactly that. */
  if (!u || isAnonymous() || !u.email) {
    if (isAuthCallback()) return;
    const fromUrl = takeUrlIntent();
    if (fromUrl) showTrial({ from: fromUrl });
    return;
  }
  const from = takeResume();
  if (!from) return;
  /* Their status first: somebody returning to a request that already went
     through should be told so, not handed the empty form a second time. */
  await loadTrialStatus().catch(() => {});
  showTrial({ from });
});

let overlay = null;
function ensureOverlay() {
  if (overlay) return overlay;
  overlay = el('div', 'authwrap hidden');
  overlay.id = 'trialwrap';
  document.body.appendChild(overlay);
  return overlay;
}
const hide = () => ensureOverlay().classList.add('hidden');
const show = () => ensureOverlay().classList.remove('hidden');

export function showTrial(opts = {}) {
  const d = door(opts.from);
  const w = ensureOverlay();
  w.innerHTML = '';
  const card = el('div', 'authcard authcard--trial');

  card.appendChild(el('h2', null, d.title));
  closeX(card, hide);

  const user = currentUser();
  const anon = !user || isAnonymous() || !user.email;

  /* No account, no address, nothing to reply to. Said as a way forward
     rather than a refusal — the sign-in card is one button away and the
     message is still there to send afterwards. */
  if (anon) {
    card.appendChild(el('p', 'authnote',
      'This is tied to an email address, so it needs an account first. '
      + 'It takes a moment, nothing you have made is lost, and we will bring '
      + 'you straight back here afterwards.'));
    const row = el('div', 'authrow');
    const cancel = el('button', 'btn btn--gh', 'Cancel');
    cancel.onclick = hide;
    const go = el('button', 'btn', 'Create an account');
    go.onclick = () => {
      markResume(opts.from);
      hide();
      showSignIn('create', { next: opts.from === 'licence' ? 'licence' : 'contact' });
    };
    row.append(cancel, go);
    card.appendChild(row);
    w.appendChild(card);
    show();
    return;
  }

  /* Already asked. Not the form again — a second identical request tells
     nobody anything, and the unique index in 0016 would refuse it anyway.
     Better to say plainly that it arrived. */
  if (trialStatus() === 'new') {
    card.appendChild(el('p', 'authnote',
      'You have already asked — it is in the queue and we will be in touch '
      + 'shortly, at ' + user.email + '.'));
    const row = el('div', 'authrow');
    const ok = el('button', 'btn', 'Close');
    ok.onclick = hide;
    row.appendChild(ok);
    card.appendChild(row);
    w.appendChild(card);
    show();
    return;
  }

  card.appendChild(el('p', 'authnote', d.lead));

  /* Shown, not offered. A disabled input would look like something that
     failed rather than something that is already settled, so this is a plain
     readout with the address in it.

     Labelled "Your account" and not "Granted to": nothing has been granted at
     this point, and a form that talks as though it has is making a promise on
     behalf of somebody who has not read the message yet. What this box needs
     to say is only which address it is being sent from, because that is the
     one that will matter later. */
  const who = el('p', 'trialwho');
  who.append(el('span', 'trialwho__label', 'Your account'),
             el('span', 'trialwho__mail', user.email));
  card.appendChild(who);

  /* Paired into two columns rather than stacked. Six full-width rows plus a
     textarea ran past the bottom of a short viewport, which put Send below the
     fold on a form whose whole job is to be sent — and a scrollable card hides
     that it is scrollable. These four are all short answers; giving each one
     the full width was never buying anything. */
  const field = (label, node, parent) => {
    const l = el('label', 'trialfield');
    l.appendChild(el('span', null, label));
    l.appendChild(node);
    (parent || card).appendChild(l);
    return node;
  };
  const pair = () => {
    const g = el('div', 'trialpair');
    card.appendChild(g);
    return g;
  };
  const text = (label, max, parent) => {
    const i = el('input', 'trialinput');
    i.type = 'text'; i.maxLength = max;
    return field(label, i, parent);
  };

  const who1 = pair();
  const name = text('Your name', 120, who1);
  const company = text('Company', 160, who1);

  const who2 = pair();
  const country = text('Country', 80, who2);

  const kind = el('select', 'trialinput');
  /* Empty first, deliberately unselected: a pre-selected first option is a
     value nobody chose, and "Agency" would then be the most common answer
     because it is the default rather than because it is true. */
  const blank = el('option', null, 'Choose…');
  blank.value = '';
  kind.appendChild(blank);
  BUSINESS_TYPES.forEach(t => {
    const o = el('option', null, t);
    o.value = t;
    kind.appendChild(o);
  });
  field('You are a…', kind, who2);

  const msg = el('textarea', 'trialinput trialmsg');
  msg.rows = 4;
  msg.maxLength = 2000;
  msg.placeholder = 'Anything you would like us to know (optional).';
  field('Message', msg);

  const err = el('p', 'note note--warn hidden');
  card.appendChild(err);

  const row = el('div', 'authrow');
  const cancel = el('button', 'btn btn--gh', 'Cancel');
  cancel.onclick = hide;
  const send = el('button', 'btn', 'Send request');
  send.onclick = async () => {
    err.classList.add('hidden');
    /* Disabled and relabelled for the round trip — a form that looks idle
       while it is working gets sent twice, and the unique index would then
       turn the second one into an error the person did not earn. */
    send.disabled = true;
    send.textContent = 'Sending…';
    try {
      await sendTrialRequest({
        name: name.value, company: company.value, country: country.value,
        businessType: kind.value, message: msg.value,
        source: opts.from === 'licence' ? 'licence' : 'motion'
      });
      hide();
      toast('Thanks — we will be in touch at ' + user.email + '.');
    } catch (e) {
      /* Into the card, not a toast: what they typed is still in the boxes and
         still worth another try, so the failure belongs beside it. */
      err.textContent = e.message;
      err.classList.remove('hidden');
      send.disabled = false;
      send.textContent = 'Send request';
    }
  };
  row.append(cancel, send);
  card.appendChild(row);

  w.appendChild(card);
  show();
  name.focus();
}
